import { FormattedMessage } from "react-intl";
import {
  makeStyles,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  tokens,
} from "@fluentui/react-components";

import type {
  DarkquellInferredSkill,
  DarkquellSkill,
  DarkquellSkillSource,
} from "@aglocal/schema/Darkquell";
import type { PredictionResponse } from "@aglocal/schema/SkillClassifier";

import titleCase from "@aglocal/web/helpers/titleCase";

import Tag, { TagGroup } from "@aglocal/web/components/Tag";
import { Text, Heading } from "@aglocal/web/components/Typography";

const useStyles = makeStyles({
  popover: {
    maxWidth: "36rem",
  },
  popoverHeader: {
    marginTop: 0,
    marginBottom: tokens.spacingVerticalS,
  },
  entitySKILL: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
  },
  entityNEG: {
    backgroundColor: tokens.colorPaletteRedBackground2,
  },
});

interface DarkquellSkillPillProps {
  skill: DarkquellSkill;
  sources: readonly DarkquellSkillSource[];
}

function DarkquellSkillPill({ skill, sources }: DarkquellSkillPillProps) {
  const styles = useStyles();
  return (
    <Popover openOnHover withArrow mouseLeaveDelay={0}>
      <PopoverTrigger>
        <Tag className={styles.entitySKILL}>{skill.name}</Tag>
      </PopoverTrigger>
      <PopoverSurface tabIndex={-1} className={styles.popover}>
        {skill.description && (
          <>
            <Heading
              as="h3"
              variant="subtitle2"
              className={styles.popoverHeader}
            >
              <FormattedMessage
                defaultMessage="Skill Description"
                description="Heading of the description shown when hovering a skill pill."
              />
            </Heading>
            <Text>{skill.description}</Text>
          </>
        )}
        {sources.length !== 0 && (
          <>
            <Heading
              as="h3"
              variant="subtitle2"
              className={styles.popoverHeader}
            >
              <FormattedMessage
                values={{ sourceCount: sources.length }}
                defaultMessage={`
                  Found in {sourceCount, plural,
                    one {# sentence}
                    other {# sentences}
                  }
                `}
                description="Heading above the transcript sentences where a skill was detected; {sourceCount} is the number of sentences."
              />
            </Heading>
            {sources.map((source, index) => (
              <Text key={index}>{source.text}</Text>
            ))}
          </>
        )}
      </PopoverSurface>
    </Popover>
  );
}

interface SkillClassifierSkillPillProps {
  skill: PredictionResponse;
}

function SkillClassifierSkillPill({ skill }: SkillClassifierSkillPillProps) {
  const styles = useStyles();

  return (
    <Popover openOnHover withArrow mouseLeaveDelay={0}>
      <PopoverTrigger>
        <Tag className={styles.entityNEG}>{titleCase(skill.skill)}</Tag>
      </PopoverTrigger>
      <PopoverSurface tabIndex={-1} className={styles.popover}>
        <FormattedMessage
          defaultMessage="Negative Skill Confidence: {confidence, number, ::percent .00}"
          description="Shown when hovering a negative skill pill; {confidence} is how confident the classifier is, as a percentage."
          values={{ confidence: skill.confidence_percent }}
        />
      </PopoverSurface>
    </Popover>
  );
}

export interface SkillPillsProps {
  skills?: readonly DarkquellInferredSkill[];
  negativeSkills?: readonly PredictionResponse[];
}

export default function SkillPills({
  skills = [],
  negativeSkills = [],
}: SkillPillsProps) {
  return (
    <TagGroup>
      {[...skills].reverse().map(({ skill, sources }) => (
        <DarkquellSkillPill key={skill.name} skill={skill} sources={sources} />
      ))}
      {[...negativeSkills].reverse().map((skill) => (
        <SkillClassifierSkillPill key={skill.skill} skill={skill} />
      ))}
    </TagGroup>
  );
}
